/**
 * The signed-in session: the tokens, who they name, and keeping them fresh.
 *
 * A small external store (subscribe / getSession — what React's
 * `useSyncExternalStore` reads), deliberately outside React: the API client's
 * token callback (`idToken`) must work from any request, not only from a
 * component render.
 *
 * - **Persistence** is one localStorage entry per auth target (`storageKey`),
 *   validated on read (it is data another build may have written, CLAUDE.md
 *   branch 2). Tokens in localStorage are readable by any script on the
 *   origin — the SPA's CSP and the absence of third-party script are what
 *   that rests on, as for any SPA holding a bearer token.
 * - **Refresh** happens when the ID token is within `REFRESH_SKEW_MS` of
 *   expiry, or when the API client forces it after a 401. Concurrent callers
 *   share one refresh. A refresh that is REFUSED, or a session that cannot be
 *   refreshed (a node's dev token), signs out and the route guard sends the
 *   user to the login page; one that fails transiently (a Wi-Fi blip, a
 *   Cognito 5xx) fails only that request, so an operator is never logged out
 *   mid-show by a network hiccup.
 * - **Other tabs**: `reload()` re-reads storage on the browser's `storage`
 *   event, so a sign-out in one tab is not written back by another's refresh.
 *   Sign-out is local (the refresh token is not revoked server-side).
 */
import { LOCAL_SESSION_RENEW_AFTER_SECONDS, LOCAL_SESSION_TTL_SECONDS } from '@acp/api-contract/offlineAuth.js';
import { z } from 'zod';
import { readTokenClaims } from '../lib/jwt.js';

/** Refresh this long before expiry, so a token never expires in flight (and clocks may disagree a little). */
export const REFRESH_SKEW_MS = 60_000;

export const sessionSchema = z
  .object({
    /** `local`: a node's local session (D20, `localSession.ts`) — what the SPA sends a node once it has one. */
    provider: z.enum(['cognito', 'dev', 'local']),
    idToken: z.string().min(1),
    accessToken: z.string().min(1).nullable(),
    refreshToken: z.string().min(1).nullable(),
    userId: z.string().min(1),
    tenantId: z.string().min(1).nullable(),
    email: z.string().nullable(),
    expiresAtMs: z.number(),
    /**
     * A node's local session held ALONGSIDE a Cognito/dev session (D20): what node calls send, while cloud calls
     * keep the upstream token. Absent or null: none (a stored session of the older shape parses as none).
     */
    local: z.object({ idToken: z.string().min(1), expiresAtMs: z.number() }).strict().nullable().optional(),
  })
  .strict();

export type Session = z.infer<typeof sessionSchema>;

/** The local session held beside an upstream one. */
export type LocalPart = { readonly idToken: string; readonly expiresAtMs: number };

/**
 * Which token a call sends (D20), and whether it must be renewed first:
 *
 *   - to the CLOUD: the session's own token — Cognito (or dev). A break-glass session (`provider: 'local'`)
 *     has no cloud token; its token is sent and the cloud refuses it, which is the cloud view being unavailable.
 *   - to a NODE: the local session held alongside, while it has life (`local-part`); else the session's own
 *     token (`session`) — a Cognito token the node accepts and answers with a new local session.
 */
export type TokenChoice = { readonly source: 'session' | 'local-part'; readonly token: string; readonly renew: boolean };

export function chooseToken(session: Session, target: 'cloud' | 'node', nowMs: number): TokenChoice {
  const local = session.local ?? null;
  if (target === 'node' && local !== null && local.expiresAtMs > nowMs) {
    return { source: 'local-part', token: local.idToken, renew: renewalDue(localAsSession(session, local), nowMs) };
  }
  return { source: 'session', token: session.idToken, renew: renewalDue(session, nowMs) };
}

/** The local part as a session of its own (what the local refresher takes and returns). */
export function localAsSession(session: Session, local: LocalPart): Session {
  return { ...session, provider: 'local', idToken: local.idToken, accessToken: null, refreshToken: null, expiresAtMs: local.expiresAtMs, local: null };
}

/**
 * Whether the session's token needs renewing before it is sent: inside the expiry skew — and, for a node's
 * local session (12 h, sliding, D20), once it is older than `LOCAL_SESSION_RENEW_AFTER_SECONDS`, so an active
 * operator's session keeps sliding forward.
 */
export function renewalDue(session: Session, nowMs: number): boolean {
  if (session.expiresAtMs - REFRESH_SKEW_MS <= nowMs) return true;
  if (session.provider !== 'local') return false;
  const issuedAtMs = session.expiresAtMs - LOCAL_SESSION_TTL_SECONDS * 1000;
  return nowMs >= issuedAtMs + LOCAL_SESSION_RENEW_AFTER_SECONDS * 1000;
}

/** A session for these tokens, identity read off the ID token; null when it is not a usable JWT. */
export function sessionFromTokens(provider: Session['provider'], idToken: string, accessToken: string | null, refreshToken: string | null): Session | null {
  const claims = readTokenClaims(idToken);
  if (claims === null) return null;
  return { provider, idToken, accessToken, refreshToken, userId: claims.userId, tenantId: claims.tenantId, email: claims.email, expiresAtMs: claims.expiresAtMs };
}

/** Key/value storage (localStorage, guarded by the caller's adapter: it may be absent or throw). */
export type StoragePort = {
  readonly get: (key: string) => string | null;
  readonly set: (key: string, value: string) => void;
  readonly remove: (key: string) => void;
};

/**
 * Renew a session: the renewed session; null when renewal is REFUSED (revoked,
 * expired, not renewable: the user signs in again); or a rejection when the
 * attempt failed transiently (offline, 5xx, throttled: the session stands).
 */
export type Refresher = (session: Session) => Promise<Session | null>;

export class NotSignedInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotSignedInError';
  }
}

export type SessionStore = {
  readonly getSession: () => Session | null;
  readonly subscribe: (listener: () => void) => () => void;
  readonly signIn: (session: Session) => void;
  readonly signOut: () => void;
  /** The token for the CLOUD (`chooseToken`), refreshed first when near expiry or `force`d. Throws `NotSignedInError`, or the refresh's own error when it failed transiently. */
  readonly idToken: (force: boolean) => Promise<string>;
  /**
   * The token for a NODE (`chooseToken`): the local session held alongside, renewed when due; `force` (after a
   * node's 401) drops it and falls back to the session's own token. With none held, it asks the node for one
   * (`exchangeLocal`) at most once a minute.
   */
  readonly nodeToken: (force: boolean) => Promise<string>;
  /** Re-read storage after another tab changed it (the browser's `storage` event; main.tsx wires it). */
  readonly reload: () => void;
};

export type SessionStoreDeps = {
  readonly storage: StoragePort;
  readonly storageKey: string;
  readonly refresh: Refresher;
  /** Renews a local session (a node's `POST /local/session` with it) — the same contract as `refresh`. */
  readonly refreshLocal: Refresher;
  /** Trades the session's own token for a node's local session; null when the node will not (or there is no node). */
  readonly exchangeLocal: ((token: string) => Promise<LocalPart | null>) | null;
  readonly nowMs: () => number;
};

/** How long after a failed exchange the store tries again (a node without a site key answers every request). */
export const LOCAL_EXCHANGE_RETRY_MS = 60_000;

function readStored(storage: StoragePort, key: string): Session | null {
  const raw = storage.get(key);
  if (raw === null) return null;
  try {
    const parsed = sessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** A renewed upstream session keeps the local session held beside the one it replaces. */
function keepingLocal(current: Session, renewed: Session): Session {
  const held = current.local ?? null;
  return held !== null && (renewed.local ?? null) === null ? { ...renewed, local: held } : renewed;
}

export function createSessionStore(deps: SessionStoreDeps): SessionStore {
  let session = readStored(deps.storage, deps.storageKey);
  const listeners = new Set<() => void>();
  // One refresh at a time PER SESSION: a refresh started for a session that
  // has since been replaced is never handed to the new session's callers.
  let inFlight: { readonly for: Session; readonly result: Promise<Session | null> } | null = null;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  const set = (next: Session | null): void => {
    session = next;
    if (next === null) deps.storage.remove(deps.storageKey);
    else deps.storage.set(deps.storageKey, JSON.stringify(next));
    notify();
  };

  const refreshOnce = (current: Session): Promise<Session | null> => {
    if (inFlight !== null && inFlight.for === current) return inFlight.result;
    const result = deps.refresh(current).finally(() => {
      if (inFlight?.result === result) inFlight = null;
    });
    inFlight = { for: current, result };
    return result;
  };

  /** The upstream session was refused: the node's local session continues if valid (D20), else sign out. Always throws. */
  const refused = (current: Session): never => {
    const local = current.local ?? null;
    if (local !== null && local.expiresAtMs > deps.nowMs()) {
      // The upstream session is gone, the node's is not: stay signed in to the node, with no cloud token.
      set(localAsSession(current, local));
      throw new NotSignedInError('the cloud session expired; this node’s local session continues');
    }
    set(null);
    throw new NotSignedInError('the session expired; sign in again');
  };

  const idToken = async (force: boolean): Promise<string> => {
    const current = session;
    if (current === null) throw new NotSignedInError('not signed in');
    if (!force && !renewalDue(current, deps.nowMs())) return current.idToken;
    // A refresh that THROWS (network, 5xx, throttling) fails this request and
    // keeps the session: the refresh token is still good and the next request
    // retries. Only a refusal (null: revoked, expired, none) signs out.
    const renewed = await refreshOnce(current);
    // Signed in or out meanwhile: answer for the session that is current now.
    if (session !== current) return idToken(false);
    if (renewed === null) return refused(current);
    set(keepingLocal(current, renewed));
    return renewed.idToken;
  };

  let exchangeFailedAtMs: number | null = null;

  const withLocal = (base: Session, local: LocalPart | null): void => {
    if (session !== base) return;
    set({ ...base, local });
  };

  /** Ask the node for a local session for `current`'s own token; its token, or null (then the own token is sent). */
  const exchange = async (current: Session, ownToken: string): Promise<string | null> => {
    if (deps.exchangeLocal === null || current.provider === 'local') return null;
    const now = deps.nowMs();
    if (exchangeFailedAtMs !== null && now - exchangeFailedAtMs < LOCAL_EXCHANGE_RETRY_MS) return null;
    const local = await deps.exchangeLocal(ownToken).catch(() => null);
    if (local === null) {
      exchangeFailedAtMs = now;
      return null;
    }
    exchangeFailedAtMs = null;
    withLocal(session ?? current, local);
    return local.idToken;
  };

  /** Renew the held local part: its new token, or — refused, or failing near its end — null (then dropped). */
  const renewLocalPart = async (current: Session, local: LocalPart): Promise<string | null> => {
    const renewed = await deps.refreshLocal(localAsSession(current, local)).catch(() => null);
    withLocal(current, renewed === null ? null : { idToken: renewed.idToken, expiresAtMs: renewed.expiresAtMs });
    return renewed?.idToken ?? null;
  };

  const nodeToken = async (force: boolean): Promise<string> => {
    const current = session;
    if (current === null) throw new NotSignedInError('not signed in');
    const local = current.local ?? null;
    if (force && local !== null) {
      // The node refused the local session (revoked, or another site's): drop only it; the own token follows.
      withLocal(current, null);
      return idToken(false);
    }
    const choice = chooseToken(current, 'node', deps.nowMs());
    if (choice.source === 'session') {
      const own = await idToken(force);
      return (await exchange(session ?? current, own)) ?? own;
    }
    if (!choice.renew || local === null) return choice.token;
    // Refused, or failing near its end: the own token carries node calls meanwhile.
    return (await renewLocalPart(current, local)) ?? idToken(false);
  };

  return {
    getSession: () => session,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    signIn: (next) => set(next),
    signOut: () => set(null),
    idToken,
    nodeToken,
    reload: () => {
      // Another tab signed in, out or refreshed: take what storage holds now,
      // without writing it back (writing back is what would undo its sign-out).
      const stored = readStored(deps.storage, deps.storageKey);
      if (JSON.stringify(stored) === JSON.stringify(session)) return;
      session = stored;
      notify();
    },
  };
}
